import { maxBotLink } from '../domain/deepLink';
import { appLink, type SignedContact, type TelegramApi } from './webapp';

/**
 * The mini app in Max (docs/agent/decisions/2026-11-01-1500-max-mini-app.md): the same surface as Telegram over
 * window.WebApp of max-web-app.js. Max passes the signed launch data in the URL fragment (#WebAppData=…) and keeps it
 * in sessionStorage for reloads; the SDK reads it from there too, so sign-in does not depend on the SDK loading.
 */

export const MAX_SDK_URL = 'https://st.max.ru/js/max-web-app.js';
const LAUNCH_KEY = 'WebAppData';
/** Settings live in localStorage: DeviceStorage does not work in the Max web client. */
const STORAGE_PREFIX = 'pass24.max.';

interface RawMaxApp {
  initData?: string;
  platform?: string | null;
  version?: string | null;
  ready?: () => void;
  requestContact?: () => Promise<{ phone?: unknown; authDate?: unknown; hash?: unknown } | undefined>;
  openLink?: (url: string) => void;
  openMaxLink?: (url: string) => void;
  shareMaxContent?: (content: { text: string; link: string }) => Promise<unknown>;
  HapticFeedback?: { notificationOccurred?: (type: 'success' | 'error' | 'warning') => unknown };
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
}

/** The signed launch data Max put into this page, or '' outside Max. */
export function maxLaunchData(win: Window = window): string {
  try {
    const fromHash = new URLSearchParams(win.location.hash.replace(/^#/, '')).get(LAUNCH_KEY);
    if (fromHash) return fromHash;
    return win.sessionStorage.getItem(LAUNCH_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Loads the Max bridge; a failure leaves the app without native buttons, not without sign-in. */
export function loadMaxSdk(doc: Document = document, src = MAX_SDK_URL): Promise<void> {
  return new Promise((resolve) => {
    const script = doc.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    doc.head.appendChild(script);
  });
}

function signedContact(value: unknown): SignedContact | false {
  const c = value as { phone?: unknown; authDate?: unknown; hash?: unknown } | null | undefined;
  if (!c || c.phone == null || c.hash == null) return false;
  return { phone: String(c.phone), authDate: String(c.authDate ?? ''), hash: String(c.hash) };
}

export function getMax(win: Window = window): TelegramApi | null {
  const app = (win as unknown as { WebApp?: RawMaxApp }).WebApp;
  const initData = app?.initData || maxLaunchData(win);
  if (!initData) return null;
  const open = (url: string) => {
    win.open(url, '_blank', 'noopener');
  };
  const dark = typeof win.matchMedia === 'function' && win.matchMedia('(prefers-color-scheme: dark)').matches;

  return {
    messenger: 'max',
    name: 'Max',
    initData,
    // Max has no theme API: the system scheme is the best guess.
    colorScheme: dark ? 'dark' : 'light',
    // Prefixed so metrics tell Max apart from Telegram and the browser, which use the same words.
    platform: `max_${app?.platform ?? 'unknown'}`,
    version: app?.version ?? '',
    ready() {
      app?.ready?.();
    },
    async requestContact() {
      if (!app?.requestContact) return false;
      try {
        return signedContact(await app.requestContact());
      } catch {
        // client.request_phone.user_refused_provide_phone_number or a client failure.
        return false;
      }
    },
    openLink(url) {
      if (app?.openLink) app.openLink(url);
      else open(url);
    },
    share(link, text) {
      const fallback = () => {
        const url = `https://max.ru/:share?text=${encodeURIComponent(`${text} ${link}`)}`;
        if (app?.openMaxLink) app.openMaxLink(url);
        else open(url);
      };
      if (!app?.shareMaxContent) return fallback();
      app.shareMaxContent({ text, link }).catch(fallback);
    },
    appLink: (startParam) => appLink(maxBotLink(), startParam),
    notify(type) {
      try {
        void Promise.resolve(app?.HapticFeedback?.notificationOccurred?.(type)).catch(() => {});
      } catch {
        // Haptics are a nicety.
      }
    },
    showBackButton(onBack) {
      const button = app?.BackButton;
      if (!button) return () => {};
      button.onClick(onBack);
      button.show();
      return () => {
        button.offClick(onBack);
        button.hide();
      };
    },
    homeScreen: {
      status: () => Promise.resolve('unsupported'),
      add() {},
    },
    storage: {
      async get(key) {
        try {
          return win.localStorage.getItem(STORAGE_PREFIX + key);
        } catch {
          return null;
        }
      },
      async set(key, value) {
        try {
          win.localStorage.setItem(STORAGE_PREFIX + key, value);
        } catch {
          // Blocked storage: the setting is asked again next time.
        }
      },
    },
  };
}
